import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, MessageCircle, Loader2, Download, Share2, GraduationCap, Info, Lock } from "lucide-react";
import { Lotus, Mandala } from "@/components/decor/Motifs";
import { generateTicketDataURL, dataURLtoBlob } from "@/lib/ticket";
import { REGISTRATION_OPEN } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    student_name: "",
    institute: "",
    student_class: "",
    address: "",
    mobile_whatsapp: "",
    mobile_alternate: "",
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", testid, required = true }) => (
    <div className="flex flex-col">
        <label
            htmlFor={name}
            className="mb-1 font-body text-xs font-semibold uppercase tracking-[0.2em] text-inkbrown-500"
        >
            {label}
            {required && <span className="text-saffron-600"> *</span>}
        </label>
        <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            data-testid={testid}
            className="w-full bg-transparent border-b-2 border-vgold-100 focus:border-saffron-500 py-3 px-1 text-inkbrown-900 font-body text-lg outline-none transition-colors placeholder:text-inkbrown-500/40"
        />
    </div>
);

export default function RegistrationForm() {
    const [form, setForm] = useState(EMPTY);
    const [showAlt, setShowAlt] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                mobile_alternate: showAlt && form.mobile_alternate ? form.mobile_alternate : null,
            };
            const { data } = await axios.post(`${API}/registrations`, payload);
            setSuccess(data);
            toast.success("Registration successful — welcome, dear soul!");
            setForm(EMPTY);
            setShowAlt(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Please verify all fields.");
        } finally {
            setSubmitting(false);
        }
    };

    const buildWaLink = () => {
        const name = success?.student_name || "Devotee";
        const number = (success?.mobile_whatsapp || "").replace(/[^\d]/g, "");
        const text = encodeURIComponent(
            `Hare Krishna! 🙏\n\nI have registered for ISKCON YOUTH MEGA FESTIVAL 2026.\n\nName: ${name}\nDate: 17 July 2026\nVenue: Nitai Gaur Temple, Sobujbag, Sreemangal\n\nJai Sri Krishna!`
        );
        // Uses wa.me deep link; user's device opens WhatsApp.
        return number
            ? `https://wa.me/${number}?text=${text}`
            : `https://wa.me/?text=${text}`;
    };

    return (
        <section
            id="register"
            className="relative px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-cream-50 overflow-hidden"
            data-testid="registration-section"
        >
            <Mandala className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] opacity-[0.05] animate-spin-slower" />

            <div className="relative mx-auto max-w-4xl">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <p className="font-body text-xs uppercase tracking-[0.35em] text-maroon-600">
                        {REGISTRATION_OPEN ? "Reserve your seat · Free" : "Registrations · Closed"}
                    </p>
                    <h2 className="mt-3 font-heading text-4xl md:text-5xl font-semibold text-inkbrown-900">
                        {REGISTRATION_OPEN ? "Register for the Festival" : "Registrations Are Now Closed"}
                    </h2>
                    <p className="mt-4 font-body text-inkbrown-500">
                        {REGISTRATION_OPEN
                            ? "Every heart is welcome. Fill in your details — we shall reserve your T-shirt, book, and a seat by the sacred yamuna of song."
                            : "The doors of registration have gently closed. Every seat is now reserved for our sanghas. See you on the day — 17 July 2026."}
                    </p>
                </div>

                <div className="relative rounded-[32px] p-[2px] bg-gradient-to-br from-vgold-300 via-saffron-400 to-vgold-500">
                    <div className="rounded-[30px] bg-white px-6 md:px-12 py-10 md:py-14 shadow-divine">
                        {!REGISTRATION_OPEN ? (
                            <ClosedPanel />
                        ) : success ? (
                            <SuccessPanel
                                data={success}
                                waLink={buildWaLink()}
                                onAgain={() => setSuccess(null)}
                            />
                        ) : (
                            <>
                                {/* Eligibility disclaimer */}
                                <div
                                    className="mb-8 rounded-2xl border-2 border-saffron-200 bg-gradient-to-br from-saffron-50 via-vgold-50 to-cream-100 p-5 md:p-6"
                                    data-testid="eligibility-disclaimer"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="grid place-items-center w-11 h-11 rounded-2xl bg-saffron-500 text-white shrink-0 shadow-glow">
                                            <GraduationCap className="w-5 h-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-body text-[10px] uppercase tracking-[0.3em] text-maroon-600 font-semibold">
                                                Who Can Register
                                            </p>
                                            <h4 className="mt-1 font-heading text-xl md:text-2xl text-inkbrown-900 font-semibold">
                                                Students Only · Class 10 to MSc / PhD
                                            </h4>
                                            <p className="mt-2 font-body text-sm md:text-base text-inkbrown-700 leading-relaxed">
                                                This festival is exclusively for currently
                                                enrolled students — from{" "}
                                                <span className="font-semibold text-maroon-600">
                                                    Class 10
                                                </span>{" "}
                                                (SSC candidates) all the way up to{" "}
                                                <span className="font-semibold text-maroon-600">
                                                    MSc, MBA, MPhil &amp; PhD scholars
                                                </span>
                                                . Please register only if you are a bona-fide
                                                student. Valid student ID may be requested at
                                                the venue.
                                            </p>
                                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                                {[
                                                    "Class 10 · SSC",
                                                    "Class 11–12 · HSC",
                                                    "Diploma",
                                                    "Bachelor's",
                                                    "MSc / MBA",
                                                    "MPhil / PhD",
                                                ].map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="inline-flex items-center rounded-full border border-vgold-300 bg-white/70 px-3 py-1 font-body text-xs text-inkbrown-700"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                                <Field
                                    label="Student's Full Name"
                                    name="student_name"
                                    value={form.student_name}
                                    onChange={onChange}
                                    placeholder="e.g. Radhika Devi"
                                    testid="input-student-name"
                                />
                                <Field
                                    label="Institute / College / School"
                                    name="institute"
                                    value={form.institute}
                                    onChange={onChange}
                                    placeholder="e.g. Sreemangal Govt. College"
                                    testid="input-institute"
                                />
                                <Field
                                    label="Class / Year You Read"
                                    name="student_class"
                                    value={form.student_class}
                                    onChange={onChange}
                                    placeholder="e.g. HSC 1st Year"
                                    testid="input-class"
                                />
                                <Field
                                    label="Mobile Number (WhatsApp)"
                                    name="mobile_whatsapp"
                                    type="tel"
                                    value={form.mobile_whatsapp}
                                    onChange={onChange}
                                    placeholder="e.g. +8801XXXXXXXXX"
                                    testid="input-mobile-whatsapp"
                                />
                                <div className="md:col-span-2">
                                    <Field
                                        label="Address"
                                        name="address"
                                        value={form.address}
                                        onChange={onChange}
                                        placeholder="Village / Town, Post Office, District"
                                        testid="input-address"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    {!showAlt ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowAlt(true)}
                                            data-testid="add-alt-mobile-btn"
                                            className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-vgold-400/60 px-4 py-2 font-body text-sm font-semibold text-saffron-700 hover:bg-vgold-50 transition-all"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add another mobile number
                                        </button>
                                    ) : (
                                        <div className="relative">
                                            <Field
                                                label="Alternate Mobile Number"
                                                name="mobile_alternate"
                                                type="tel"
                                                value={form.mobile_alternate}
                                                onChange={onChange}
                                                placeholder="Optional additional contact"
                                                testid="input-mobile-alternate"
                                                required={false}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowAlt(false);
                                                    setForm((f) => ({ ...f, mobile_alternate: "" }));
                                                }}
                                                data-testid="remove-alt-mobile-btn"
                                                className="absolute right-0 top-0 text-inkbrown-500 hover:text-maroon-600"
                                                aria-label="Remove alternate mobile"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="md:col-span-2 mt-4 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <p className="font-body text-sm text-inkbrown-500 italic max-w-md">
                                        By registering, you enter a family bound by song
                                        and service. We shall message you closer to the
                                        day on the number you shared.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="submit-registration-btn"
                                        className="group relative overflow-hidden rounded-full bg-saffron-500 px-10 py-4 font-body font-bold uppercase tracking-[0.2em] text-white shadow-glow transition-all hover:-translate-y-1 hover:bg-saffron-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Blessing your seat…
                                            </span>
                                        ) : (
                                            "Confirm My Registration"
                                        )}
                                    </button>
                                </div>
                            </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

function SuccessPanel({ data, waLink, onAgain }) {
    const [ticketUrl, setTicketUrl] = useState(null);
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const url = await generateTicketDataURL(data);
                if (alive) setTicketUrl(url);
            } catch (e) {
                // ticket generation failed silently; success panel still works
            }
        })();
        return () => {
            alive = false;
        };
    }, [data]);

    const filename = useMemo(
        () => `iys-festival-ticket-${(data.student_name || "devotee").replace(/\s+/g, "-").toLowerCase()}.png`,
        [data]
    );

    const downloadTicket = () => {
        if (!ticketUrl) return;
        const a = document.createElement("a");
        a.href = ticketUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Ticket saved. Share it far and wide!");
    };

    const shareTicket = async () => {
        if (!ticketUrl) return;
        setSharing(true);
        try {
            const blob = dataURLtoBlob(ticketUrl);
            const file = new File([blob], filename, { type: "image/png" });
            const shareData = {
                title: "ISKCON Youth Mega Festival 2026",
                text: `Hare Krishna! 🙏 I have registered for ISKCON YOUTH MEGA FESTIVAL 2026.\n📅 17 July 2026\n📍 Nitai Gaur Temple, Sreemangal\nRegistration is FREE — join us!`,
                files: [file],
            };
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share(shareData);
                toast.success("Shared!");
            } else {
                downloadTicket();
                toast.info("Ticket downloaded — attach it on WhatsApp / Facebook.");
            }
        } catch (e) {
            if (e?.name !== "AbortError") {
                downloadTicket();
            }
        } finally {
            setSharing(false);
        }
    };

    return (
        <div className="text-center py-6" data-testid="registration-success-panel">
            <div className="mx-auto w-20 h-20 rounded-full bg-saffron-50 border border-saffron-200 flex items-center justify-center animate-float">
                <CheckCircle2 className="w-10 h-10 text-saffron-600" />
            </div>
            <div className="flex items-center justify-center my-4">
                <Lotus className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-inkbrown-900">
                Hari bol, {data.student_name}!
            </h3>
            <p className="mt-3 font-body text-inkbrown-500 max-w-lg mx-auto">
                Your seat at ISKCON Youth Mega Festival 2026 is reserved. Here is your
                divine e-ticket — save it, share it, bring it on the day.
            </p>

            {/* Ticket preview */}
            {ticketUrl && (
                <div className="mt-8 flex justify-center">
                    <div className="relative max-w-[340px] w-full rounded-2xl overflow-hidden border-2 border-vgold-400 shadow-glow" data-testid="ticket-preview">
                        <img
                            src={ticketUrl}
                            alt="Your ISKCON Festival 2026 e-ticket"
                            className="w-full h-auto block"
                        />
                    </div>
                </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
                <button
                    onClick={shareTicket}
                    disabled={!ticketUrl || sharing}
                    data-testid="share-ticket-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-saffron-500 to-vgold-500 text-inkbrown-900 px-7 py-3 font-body font-bold uppercase tracking-widest text-sm shadow-glow hover:-translate-y-1 transition-all disabled:opacity-60"
                >
                    {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Share Ticket
                </button>
                <button
                    onClick={downloadTicket}
                    disabled={!ticketUrl}
                    data-testid="download-ticket-btn"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-vgold-400/70 px-7 py-3 font-body font-semibold uppercase tracking-widest text-sm text-inkbrown-900 hover:bg-vgold-50 transition-all disabled:opacity-60"
                >
                    <Download className="w-4 h-4" /> Download
                </button>
                <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-confirmation-link"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-3 font-body font-semibold uppercase tracking-widest text-sm hover:-translate-y-1 transition-all"
                >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <button
                    onClick={onAgain}
                    data-testid="register-another-btn"
                    className="rounded-full border-2 border-inkbrown-500/40 px-7 py-3 font-body font-semibold uppercase tracking-widest text-sm text-inkbrown-500 hover:bg-cream-100 transition-all"
                >
                    Register another
                </button>
            </div>

            <p className="mt-6 font-body text-xs text-inkbrown-500/80 italic max-w-md mx-auto">
                Tip: post the ticket on your WhatsApp status or Facebook story — spread the flute-song of Krishna to your friends.
            </p>
        </div>
    );
}

function ClosedPanel() {
    return (
        <div className="text-center py-8" data-testid="registration-closed-panel">
            <div className="mx-auto w-20 h-20 rounded-full bg-vgold-50 border border-vgold-200 flex items-center justify-center animate-float">
                <Lock className="w-9 h-9 text-vgold-700" />
            </div>
            <div className="flex items-center justify-center my-5">
                <Lotus className="w-8 h-8" />
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-inkbrown-900">
                Registration is closed
            </h3>
            <p className="mt-4 font-body text-inkbrown-500 max-w-lg mx-auto leading-relaxed">
                Thank you, dear devotees — every seat has been lovingly reserved.
                If you have already registered, please save your e-ticket and join us
                on <span className="font-semibold text-maroon-600">17 July 2026</span> at
                Nitai Gaur Temple, Sreemangal.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
                <div className="rounded-2xl bg-saffron-50 border border-saffron-200 p-5">
                    <p className="font-body text-[10px] uppercase tracking-[0.3em] text-maroon-600">
                        Festival Day
                    </p>
                    <p className="mt-1 font-heading text-xl text-inkbrown-900">
                        17 July 2026 · 10 AM
                    </p>
                    <p className="mt-1 font-body text-sm text-inkbrown-500">
                        Nitai Gaur Temple, Sobujbag, Sreemangal
                    </p>
                </div>
                <div className="rounded-2xl bg-cream-100 border border-vgold-200 p-5">
                    <p className="font-body text-[10px] uppercase tracking-[0.3em] text-maroon-600">
                        Emergency Contact
                    </p>
                    <a
                        href="tel:+8801721607559"
                        className="mt-1 inline-block font-heading text-xl text-inkbrown-900 hover:text-saffron-600 transition-colors"
                    >
                        +880 1721-607559
                    </a>
                    <p className="mt-1 font-body text-sm text-inkbrown-500">
                        ISKCON Youth Services, Sreemangal
                    </p>
                </div>
            </div>

            <p className="mt-8 font-heading italic text-lg text-inkbrown-500">
                “Hare Krishna — see you on the day.”
            </p>
        </div>
    );
}
